import { Controller } from "@hotwired/stimulus"
import Cookies from "js-cookie";
import { createRuleHandler } from "./survey_bar_rules";

export default class extends Controller {
  static targets = [ "close" ];
  static values = {
    rules: Object,
    default: {
      min_page_visits: 3,
      page_rules: []
    }
  };

  // Storage key mapping based on rule types
  STORAGE_KEYS = {
    page_visit: 'survey_bar_page_visit_count',
    page_timeout: 'survey_bar_page_timeout_completed',
    visit_after: 'survey_bar_visited_after_required_page',
    scroll_depth: 'survey_bar_scrolled_required_depth',
    page_views: 'survey_bar_page_views_data',
    time_on_site: 'survey_bar_time_on_site_data',
  }

  #countedCurrentVisit = false;

  connect() {
    this.ruleHandlers = {};

    this.debugMode = window.location.search.includes('debug');
    this._debugLog('Survey Bar Controller connected', this.rulesValue);

    // Maak rule handlers aan
    this._initRuleHandlers();

    this._shouldShow();

    this.rulesValueChanged = this.rulesValueChanged.bind(this);
  }

  disconnect() {
    // Cleanup handlers
    Object.values(this.ruleHandlers).forEach(handler => {
      if (handler.cleanup) handler.cleanup();
    });
  }

  close() {
    this.track("close");
    localStorage.setItem('survey_bar_closed', 'true');
    this.element.remove();
  }

  trackLinkClick() {
    this.track("link_click");

    this.element.classList.add('translate-y-96');
    localStorage.setItem('survey_bar_closed', 'true');
  }

  track(action) {
    trackEvent({
      exp_event_cat: "survey_bar",
      exp_event_act: action,
    });
  }

  rulesValueChanged() {
    this.ruleHandlers = {};
    this._initRuleHandlers();
    this._shouldShow();
  }

  _shouldShow() {
    // when the query param ?surveybar is present, show the survey bar
    if (window.location.search.includes('surveybar')) {
      this._debugLog('Showing survey bar because of survey_bar=true in URL');
      this._showSurveyBar();
      return;
    }

    if (this._hasUserInteracted()) {
      this._debugLog('User has already interacted with the site, not showing survey bar');
      this.element.remove();
      return;
    }

    const visitData = this._getVisitData();
    this._debugLog('Current visit data:', visitData);
    this._trackCurrentVisit(visitData.visitCount);
    this._processPageRules(visitData);

    if (this._shouldShowSurvey(visitData)) {
      this._debugLog('Showing survey bar - all conditions met');
      this._showSurveyBar();
    } else {
      this._debugLog('Not showing survey bar - conditions not met');
      this._hideSurveyBar();
    }
  }

  _showSurveyBar() {
    // only show the survey bar once
    if (this.element.classList.contains('flex')) return;

    this.element.classList.remove('hidden');
    this.element.classList.add('flex');
    this.track("show");
  }

  _hideSurveyBar() {
    this.element.classList.add('hidden');
    this.element.classList.remove('flex');
  }

  _hasUserInteracted() {
    const loginClicks = parseInt(Cookies.get('login_click_count') || '0');
    const signupCount = parseInt(Cookies.get('sign_up_count') || '0');
    return loginClicks > 0 || signupCount > 0;
  }

  _getVisitData() {
    const data = {
      visitCount: parseInt(localStorage.getItem(this.STORAGE_KEYS.page_visit) || '0'),
      hasClosed: localStorage.getItem('survey_bar_closed') === 'true',
    };

    // Add all rule storage keys to the data object
    Object.values(this.STORAGE_KEYS).forEach(key => {
      // Convert storage key to data property name
      const propName = this._storageKeyToPropertyName(key);
      if (key.endsWith('_data')) {
        // For complex data stored as JSON
        try {
          data[propName] = JSON.parse(localStorage.getItem(key) || '{}');
        } catch (e) {
          data[propName] = {};
        }
      } else {
        // For simple boolean flags
        data[propName] = localStorage.getItem(key) === 'true';
      }
    });

    return data;
  }

  _processPageRules(visitData) {
    const { page_rules } = this.rulesValue;
    if (!page_rules || !Array.isArray(page_rules)) return;

    this._debugLog('Processing page rules:', page_rules);

    page_rules.forEach(rule => {
      const handler = this.ruleHandlers[rule.type];
      if (handler) {
        this._debugLog(`Processing rule of type: ${rule.type}`, rule);
        handler.process(rule, visitData);
      } else {
        this._debugLog(`No handler for rule type: ${rule.type}`);
      }
    });
  }

  _shouldShowSurvey(visitData) {
    // Base condition: minimum page visits and not closed
    if (visitData.visitCount < this.rulesValue.min_page_visits) {
      this._debugLog(`Not enough page visits: ${visitData.visitCount} < ${this.rulesValue.min_page_visits}`);
      return false;
    }

    if (visitData.hasClosed) {
      this._debugLog('Survey was previously closed by user');
      return false;
    }

    // Check all required rule conditions
    const { page_rules } = this.rulesValue;
    if (!page_rules || !Array.isArray(page_rules)) {
      this._debugLog('No page rules defined, showing survey');
      return true;
    }

    let allRulesMet = true;

    page_rules.forEach(rule => {
      const handler = this.ruleHandlers[rule.type];
      let ruleResult = false;

      if (handler) {
        ruleResult = handler.evaluate(visitData);
      } else {
        this._debugLog(`No handler for rule type: ${rule.type}`);
        ruleResult = true; // Default true voor onbekende regels
      }

      this._debugLog(`Rule ${rule.type} result: ${ruleResult}`, rule);

      if (!ruleResult) {
        allRulesMet = false;
      }
    });

    this._debugLog(`All required rules met: ${allRulesMet}`);
    return allRulesMet;
  }

  _initRuleHandlers() {
    const { page_rules } = this.rulesValue;
    if (!page_rules || !Array.isArray(page_rules)) return;

    // Maak handlers voor elk rule type dat we tegenkomen
    page_rules.forEach(rule => {
      if (!this.ruleHandlers[rule.type]) {
        this.ruleHandlers[rule.type] = createRuleHandler(rule.type, this);
      }
    });

    // Initialiseer time tracking indien nodig
    if (this.ruleHandlers['time_on_site']) {
      this.ruleHandlers['time_on_site'].initTracking();
    }
  }

  findRuleByType(type) {
    const { page_rules } = this.rulesValue;
    if (!page_rules || !Array.isArray(page_rules)) return null;
    return page_rules.find(rule => rule.type === type);
  }

  _storageKeyToPropertyName(key) {
    // survey_bar_page_timeout_completed -> pageTimeoutCompleted
    return key.replace(/survey_bar_/g, '')
              .replace(/_([a-z])/g, (m, p1) => p1.toUpperCase());
  }

  _trackCurrentVisit(visitCount) {
    if (this.#countedCurrentVisit) return;

    localStorage.setItem(this.STORAGE_KEYS.page_visit, visitCount + 1);
    this.#countedCurrentVisit = true;
  }

  // Debug logger method
  _debugLog(...args) {
    if (this.debugMode) {
      console.info('[Survey Bar Debug]', ...args);
    }
  }
}
